import styled from "styled-components";
import dayjs from "dayjs";

import { Icon } from "components/atoms";

import { Statement } from "interfaces/statements";
import { IconName } from "interfaces/icons";

import StatementsClient from "services/api/statements";

import { MAIN_THEME } from "styles/colors";
import { FONT_FAMILY_MEDIUM, FONT_WEIGHT_MEDIUM } from "styles/typography";

interface TableProps {
	columns?: string;
}

const DownloadLink = styled.button`
	border: none;
	outline: none;
	cursor: pointer;
	background: transparent;
	color: ${MAIN_THEME.SECONDARY};
	font-family: ${FONT_FAMILY_MEDIUM};
	font-weight: ${FONT_WEIGHT_MEDIUM};
	transition: opacity 0.15s ease;

	&:hover {
		opacity: 0.8;
		text-decoration: underline;
	}
`;

const TableCell = styled.div`
	color: ${MAIN_THEME.TEXT_SECONDARY};
	display: flex;
	align-items: center;
	font-size: 14px;
	justify-content: flex-start;
	text-align: left;
	min-height: 52px;
	padding: 10px 8px;
	background: transparent;
	border: none;
	border-bottom: 1px solid ${MAIN_THEME.BORDER_COLOR};
	transition: background 0.15s ease;

	@media (max-width: 768px) {
		font-size: 13px;
		min-height: 44px;
		padding: 8px 6px;
	}
`;

const Table = styled.div<TableProps>`
	display: grid;
	grid-template-columns: ${(props) => props.columns || '1fr 1fr 1fr 1fr 1fr 1fr 0.8fr'};
	gap: 0;

	&:hover {
		${TableCell} {
			background-color: ${MAIN_THEME.TABLE_HOVER};
		}
	}
`;

const BaseButton = styled.button`
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	border-radius: 100%;
	height: 28px;
	width: 28px;
	display: flex;
	justify-content: center;
	align-items: center;
	background: ${MAIN_THEME.DARK_BG_TERTIARY};
	color: ${MAIN_THEME.TEXT_PRIMARY};
	cursor: pointer;
	margin: 0 auto;
	transition: background 0.15s ease, border-color 0.15s ease;

	&:hover {
		background: ${MAIN_THEME.TABLE_HOVER};
	}
`;

const EditButton = styled(BaseButton)`
	border: 1px solid ${MAIN_THEME.GREY_DARK};
`;

interface Props {
	data: Statement;
	onSendClick?: (statement: Statement) => void;
	hideEditAction?: boolean;
	hideDeleteAction?: boolean;
	showProperty?: boolean;
	visibleColumnIds?: string[];
	gridTemplateColumns?: string;
}

const StatementListItem = (props: Props): JSX.Element => {
	const { data, onSendClick, hideEditAction, hideDeleteAction, showProperty, visibleColumnIds, gridTemplateColumns } =
		props;

	const { month, year, reportData, sent, createdAt, reviewed, propertyId, id } =
		data;

	const handleSendClick = () => {
		if (onSendClick) {
			onSendClick(data);
		}
	};

	const handleDonwload = async () => {
		try {
			const res = await StatementsClient.getStatementDownloadLink({
				propertyId,
				statementId: id,
			});

			if (res.status === 200) {
				const { url } = res.data;

				const link = document.createElement("a");

				link.setAttribute("download", "report");
				link.setAttribute("target", "_blank");
				link.href = url;

				document.body.appendChild(link);

				link.click();
			}
		} catch (error) {
			console.log({ error });
		}
	};

	const defaultGridColumns = showProperty
		? '1fr 1fr 1fr 1fr 1fr 1fr 1fr 0.8fr'  // 8 columns with Property
		: '1fr 1fr 1fr 1fr 1fr 1fr 0.8fr';     // 7 columns without Property

	const isVisible = (id: string) => {
		if (!visibleColumnIds) return true;
		return visibleColumnIds.includes(id);
	}

	return (
		<Table columns={gridTemplateColumns || defaultGridColumns}>
			{showProperty && isVisible('property') ? <TableCell>{data?.propertyName || ""}</TableCell> : null}
			{isVisible('period') && <TableCell>
				{`${dayjs()
					.month(+month - 1)
					.format("MMMM")}, ${year}`}
			</TableCell>}
			{isVisible('reservations') && <TableCell>{reportData.reportItems.length}</TableCell>}
			{isVisible('income') && <TableCell>
				{reportData.calculations.totalInCurrency.totalIncomeInCurrency}
			</TableCell>}
			{isVisible('expenses') && <TableCell>
				{reportData.calculations.totalInCurrency.totalExpensesInCurrency}
			</TableCell>}
			{isVisible('paidOut') && <TableCell>
				{reportData.calculations.totalInCurrency.netOwnerIncomeInCurrency}
			</TableCell>}
			{isVisible('createdAt') && <TableCell>{dayjs(createdAt).format("D MMM, YYYY")}</TableCell>}
			{isVisible('viewInvoice') && <TableCell>
				<DownloadLink onClick={handleDonwload}>PDF</DownloadLink>
			</TableCell>}
		</Table>
	);
};

export default StatementListItem;
